import { createReducer, on } from '@ngrx/store';

import {
  ADVANCED_FILTER_TYPE,
  RESPONDENT_LIST_ORIGIN,
  Survey,
  SurveyKind,
  SurveyLaunchFormMapper,
  SurveyLaunchFormState,
  SurveySameSettings,
} from '@ps/survey-utils';

import {
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_LAUNCH_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationLaunchState } from '../survey-creation-state.model';
import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationMcApiActions,
  SurveyCreationTrackerApiActions,
} from '../audience/survey-creation-audience.actions';
import { omitChild } from '../audience/utils/audience-children.helper';
import { resolveLoadedParticipant } from '../core/utils/core-children.helper';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';
import {
  initializeLaunchState,
  isLoadedLaunchSibling,
  keyedLoadedLaunchChildren,
  patchChildFilterAudience,
  patchChildren,
  patchRespondentListUploads,
  registerLaunchChildren,
  sharesLaunchSettingsIn,
  withSharedLaunchSettingsAcrossChildren,
} from './utils/launch-children.helper';
import {
  CHILD_SCOPE,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';

function syncedFromSurvey(
  child: SurveyLaunchFormState,
  survey: Survey,
): SurveyLaunchFormState {
  return {
    value: new SurveyLaunchFormMapper().fromSurvey(survey),
    valid: child.valid,
    errors: child.errors,
    isDirty: child.isDirty,
  };
}

export const surveyCreationLaunchReducer = createReducer(
  INITIAL_SURVEY_CREATION_LAUNCH_STATE,

  // Cross-listens to both stage-specific init actions — the save pipeline
  // reads launch child state regardless of which page opened it.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, action): SurveyCreationLaunchState => {
      const { surveyId } = action;
      const { rootSurveyId } = coreContextOf(action);

      const alreadyTargeted = !!surveyId && rootSurveyId === surveyId;

      if (alreadyTargeted) {
        return state;
      }

      // Legacy: a locale switch keeps the launch form and its "same …"
      // checkboxes; only the per-survey launch data starts over.
      if (!!surveyId && isLoadedLaunchSibling(state.children, surveyId)) {
        return {
          ...state,
          pricing: null,
          isPricingLoading: false,
          isLaunching: false,
          aidaEligibility: null,
        };
      }

      return initializeLaunchState(surveyId);
    },
  ),

  // A Group MC locale added on the page is held as its survey saved it, so the
  // next project save sends it its own launch rather than an empty one.
  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    (state, action): SurveyCreationLaunchState => {
      const registered = registerLaunchChildren(state, [action.survey.id]);
      return sharesLaunchSettingsIn(coreContextOf(action))
        ? {
            ...registered,
            children: {
              ...registered.children,
              ...keyedLoadedLaunchChildren([action.survey]),
            },
          }
        : registered;
    },
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationLaunchState => ({
      ...state,
      children: omitChild(state.children, surveyId),
      respondentListUploads: omitChild(state.respondentListUploads, surveyId),
    }),
  ),

  // Ticking a box shares the edited child's section with the others at once.
  on(
    SurveyCreationLaunchActions.updateSameSettings,
    (state, action): SurveyCreationLaunchState =>
      withSharedLaunchSettingsAcrossChildren(
        { ...state, sameSettings: action.sameSettings },
        coreContextOf(action),
      ),
  ),

  // The Launch page resets this slice when it is left, and every save — Define
  // Audience included — PUTs the ACTIVE child's launch form, so a child left at
  // its defaults would overwrite on the API what the Launch page saved. Survey
  // loads therefore rebuild the child's value from the loaded survey (the GET
  // is the source of truth). A server sync, not a user edit: valid, errors and
  // isDirty are carried over as the child holds them. A switch saves before it
  // loads, so no unsaved edit is replaced.
  //
  // A project's root is a different survey than its active child, so the root
  // is seeded here; any other survey is its own active child, seeded by the
  // active load that always follows, so it is not built twice.
  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, action): SurveyCreationLaunchState =>
      SurveyKind.isProject(action.survey)
        ? patchChildren(
            state,
            coreContextOf(action),
            CHILD_SCOPE.keys([action.survey.id]),
            (child) => syncedFromSurvey(child, action.survey),
          )
        : state,
  ),

  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, (child) =>
        syncedFromSurvey(child, action.survey),
      ),
  ),

  // Batch-loaded children (Grouped MC / Traditional MC locales, Tracker waves)
  // not already seeded by the root/active load are seeded from their own
  // survey, so a sibling save never PUTs a default launch form. The family's
  // "same …" boxes are decided here, once per load, from the children's saved
  // values; a Traditional MC locale switch reuses the loaded family and keeps them.
  on(
    SurveyCreationCoreChildrenApiActions.loadSpecificAudienceParentSuccess,
    (state, { parentId }): SurveyCreationLaunchState =>
      registerLaunchChildren(state, [parentId]),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, { surveys }): SurveyCreationLaunchState => {
      const unseeded = surveys.filter(({ id }) => !state.children[id]);
      const registered = registerLaunchChildren(
        state,
        unseeded.map(({ id }) => id),
      );

      return {
        ...registered,
        sameSettings: SurveySameSettings.for(
          surveys.filter((survey) => !!resolveLoadedParticipant(survey)),
        ).choices(),
        children: {
          ...registered.children,
          ...keyedLoadedLaunchChildren(unseeded),
        },
      };
    },
  ),

  on(
    SurveyCreationTrackerApiActions.updateLaunchDateSuccess,
    (state, { surveys }): SurveyCreationLaunchState => ({
      ...state,
      children: {
        ...state.children,
        ...keyedLoadedLaunchChildren(surveys),
      },
    }),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, (child) =>
        patchChildFilterAudience(child, {
          includeExcludeRespondentsEnabled:
            action.filters.type === ADVANCED_FILTER_TYPE.INCLUDE,
        }),
      ),
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state, action): SurveyCreationLaunchState => {
      const fromKey =
        coreContextOf(action).activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY;
      return {
        ...state,
        children: delegateRecordToDefaultChild(state.children, fromKey),
        respondentListUploads: delegateRecordToDefaultChild(
          state.respondentListUploads,
          fromKey,
        ),
      };
    },
  ),

  // While a box is ticked, every edit of the active child is shared with the others.
  on(
    SurveyCreationLaunchActions.updateForm,
    (state, action): SurveyCreationLaunchState =>
      withSharedLaunchSettingsAcrossChildren(
        patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
          ...action.form,
        }),
        coreContextOf(action),
      ),
  ),

  // Server sync, not a user edit — validity, errors and dirtiness stay as the
  // child already holds them so an untouched launch form is never marked dirty.
  on(
    SurveyCreationLaunchActions.patchSurveyUrls,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          value: { ...child.value, surveyUrls: action.surveyUrls },
          valid: child.valid,
          errors: child.errors,
          isDirty: child.isDirty,
        }),
      ),
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    (state, action): SurveyCreationLaunchState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          ...child,
          isDirty: false,
        }),
      ),
  ),

  on(
    SurveyCreationLaunchActions.loadLaunchPricing,
    (state): SurveyCreationLaunchState => ({
      ...state,
      pricing: null,
      isPricingLoading: true,
      isLaunching: false,
    }),
  ),

  on(
    SurveyCreationLaunchApiActions.launchPricingLoaded,
    (state, { pricing }): SurveyCreationLaunchState => ({
      ...state,
      pricing,
      isPricingLoading: false,
    }),
  ),

  on(
    SurveyCreationLaunchApiActions.launchPricingFailure,
    (state): SurveyCreationLaunchState => ({
      ...state,
      pricing: null,
      isPricingLoading: false,
    }),
  ),

  on(
    SurveyCreationLaunchActions.loadAidaEligibility,
    SurveyCreationLaunchApiActions.aidaEligibilityFailure,
    (state): SurveyCreationLaunchState => ({ ...state, aidaEligibility: null }),
  ),

  on(
    SurveyCreationLaunchApiActions.aidaEligibilityLoaded,
    (state, { eligibility }): SurveyCreationLaunchState => ({
      ...state,
      aidaEligibility: eligibility,
    }),
  ),

  on(
    SurveyCreationLaunchActions.launchSurvey,
    (state): SurveyCreationLaunchState => ({ ...state, isLaunching: true }),
  ),

  // A failed save ends the launch attempt too — the status change never runs.
  on(
    SurveyCreationLaunchApiActions.launchSurveySuccess,
    SurveyCreationLaunchApiActions.launchSurveyFailure,
    SurveyCreationCoreApiActions.saveSurveyFailure,
    (state): SurveyCreationLaunchState => ({ ...state, isLaunching: false }),
  ),

  on(
    SurveyCreationLaunchActions.uploadRespondentList,
    SurveyCreationLaunchActions.uploadUniqueLinkPsidFile,
    SurveyCreationLaunchActions.loadRespondentList,
    SurveyCreationLaunchActions.resolveRespondentFilters,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: true, error: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.uploadRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.items,
          origin: RESPONDENT_LIST_ORIGIN.BUYER_UPLOAD,
        },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.loadRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.items,
          origin: action.origin,
        },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.resolveRespondentFiltersSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        {
          loading: false,
          error: null,
          items: action.filters.psidRefs,
          origin: action.filters.psidRefs.length
            ? RESPONDENT_LIST_ORIGIN.SURVEY_GROUPING
            : null,
        },
      ),
  ),

  // The parsed rows live with the advanced filters (audience slice); this
  // slice only tracks the upload's progress for the upload manager.
  on(
    SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.uploadRespondentListFailure,
    SurveyCreationLaunchApiActions.uploadUniqueLinkPsidFileFailure,
    SurveyCreationLaunchApiActions.loadRespondentListFailure,
    SurveyCreationLaunchApiActions.resolveRespondentFiltersFailure,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: action.error },
      ),
  ),

  on(
    SurveyCreationLaunchActions.clearRespondentList,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: true, error: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.clearRespondentListSuccess,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: null, items: [], origin: null },
      ),
  ),

  on(
    SurveyCreationLaunchActions.restoreSavedGrouping,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: null, items: [], origin: null },
      ),
  ),

  on(
    SurveyCreationLaunchApiActions.clearRespondentListFailure,
    (state, action): SurveyCreationLaunchState =>
      patchRespondentListUploads(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        { loading: false, error: action.error },
      ),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationLaunchState => INITIAL_SURVEY_CREATION_LAUNCH_STATE,
  ),
);
